/**
 * Handles the intersection changes for observed elements.
 * When an element enters the viewport, it becomes visible and its animation starts.
 * When an element leaves the viewport, it becomes hidden and its animation state is reset.
 * @param {IntersectionObserverEntry[]} entries - An array of IntersectionObserverEntry objects.
 * @param {IntersectionObserver} observer - The IntersectionObserver instance.
 */
const handleIntersection = (entries, observer) => {
	entries.forEach(entry => {
		const element = entry.target;
		const animationClass = element.dataset.animationClass; // Get animation class from data-animation-class
		const animationDelay = element.dataset.animationDelay; // Get animation delay from data-animation-delay
		const animationThreshold = parseFloat(element.dataset.animationThreshold) || 0.15; // Get custom threshold from data-animation-threshold (optional)
		const animationHideDelay = parseFloat(element.dataset.animationHideDelay) || 900; // Optional hide delay (ms) to prevent flicker
		const animationMinVisible = parseFloat(element.dataset.animationMinVisible) || 80; // Optional minimum visible pixels to treat as "visible enough"

		// Accessibility: Respect reduced motion preference
		// If user prefers reduced motion, do not animate and do not hide elements.
		if (window.__aosPrefersReducedMotion) {
			element.style.opacity = '';
			element.style.pointerEvents = '';
			element.style.animationDelay = '';
			element.classList.remove(animationClass, 'animated');

			if (element._aosHideTimer) {
				clearTimeout(element._aosHideTimer);
				element._aosHideTimer = null;
			}

			observer.unobserve(element);
			return;
		}

		// For tall elements intersectionRatio can be unstable / too small.
		// Use visible pixels as an additional stable condition.
		const visiblePx = entry.intersectionRect ? entry.intersectionRect.height : 0;

		const isVisibleEnough =
			entry.isIntersecting &&
			(
				entry.intersectionRatio >= animationThreshold ||
				visiblePx >= animationMinVisible
			);

		// If element becomes visible enough
		if (isVisibleEnough) {

			// Cancel any scheduled hide
			if (element._aosHideTimer) {
				clearTimeout(element._aosHideTimer);
				element._aosHideTimer = null;
			}

			// Avoid re-triggering animation if already shown
			if (element._aosShown) return;

			// Use opacity + animation instead of visibility so keyboard focus is preserved.
			// Animations override inline styles, so clearing opacity lets the keyframe take over.
			element.style.animationDelay = animationDelay;
			element.classList.add(animationClass, 'animated');
			element.style.opacity = '';
			element.style.pointerEvents = '';

			element._aosShown = true;
		} else {

			// If already hidden or hide already scheduled – do nothing
			if (!element._aosShown || element._aosHideTimer) return;

			// Schedule hide with delay to prevent bottom-edge flicker
			element._aosHideTimer = setTimeout(() => {

				element.classList.remove(animationClass, 'animated');
				element.style.animationDelay = '';
				// opacity:0 + pointer-events:none hides visually but keeps element in tab order
				element.style.opacity = '0';
				element.style.pointerEvents = 'none';

				element._aosShown = false;

				// No forced reflow here: `remove` (this timeout) and the next `add` (a later,
				// separate IntersectionObserver callback) are always separated by real render
				// frames during scroll, so the animation replays correctly without it. Forcing
				// one per element here caused O(n) layout thrashing on fast back-and-forth
				// scrolling — the root cause of scroll jank / heating on iOS Safari.
				element._aosHideTimer = null;

			}, animationHideDelay);
		}
	});
};

// Configuration options for the Intersection Observer.
const observerOptions = {
	root: null, // The viewport is used as the root (the element whose scroll area is used for observing targets).
	rootMargin: '0px 0px -10% 0px', // Slight bottom margin reduction to reduce edge flicker.
	threshold: [0, 0.05, 0.15, 0.3, 1] // Fewer thresholds reduce excessive callbacks and flicker near edges.
};

// Create a new Intersection Observer instance, if supported. Templates render these elements
// with inline `style="visibility: hidden;"` as their no-JS state, so any unguarded failure here
// (e.g. on browsers without IntersectionObserver, like unpatched old iOS Safari) would throw
// during module load and leave the content hidden forever — i.e. the page appears blank.
const supportsIntersectionObserver = 'IntersectionObserver' in window;
const observer = supportsIntersectionObserver
	? new IntersectionObserver(handleIntersection, observerOptions)
	: null;

function _aosInit() {
	// Accessibility: Respect reduced motion preference
	const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
	window.__aosPrefersReducedMotion = !!mediaQuery.matches;

	const animatableElements = document.querySelectorAll('[data-animation-class]');

	function revealAll(elements) {
		elements.forEach(element => {
			element.style.visibility = '';
			element.style.opacity = '';
			element.style.pointerEvents = '';
			element.style.animationDelay = '';
			element.classList.remove(element.dataset.animationClass, 'animated');
			if (element._aosHideTimer) {
				clearTimeout(element._aosHideTimer);
				element._aosHideTimer = null;
			}
			if (observer) observer.unobserve(element);
		});
	}

	// No IntersectionObserver support: reveal everything immediately, no scroll animation.
	if (!supportsIntersectionObserver) {
		revealAll(animatableElements);
		return;
	}

	// If user prefers reduced motion, show everything immediately and skip observer
	if (window.__aosPrefersReducedMotion) {
		revealAll(animatableElements);
		return;
	}

	// If preference changes while page is open, update behavior
	const onReducedMotionChange = (matches) => {
		window.__aosPrefersReducedMotion = !!matches;
		if (window.__aosPrefersReducedMotion) {
			revealAll(document.querySelectorAll('[data-animation-class]'));
		}
	};

	if (mediaQuery.addEventListener) {
		mediaQuery.addEventListener('change', (e) => onReducedMotionChange(e.matches));
	} else if (mediaQuery.addListener) {
		mediaQuery.addListener((e) => onReducedMotionChange(e.matches));
	}

	// opacity:0 + pointer-events:none hides visually while keeping elements in the
	// keyboard tab order — unlike visibility:hidden which removes them entirely.
	animatableElements.forEach(element => {
		element._aosShown = false;
		element._aosHideTimer = null;

		// Clear any visibility:hidden set inline in the template, then use opacity instead.
		element.style.visibility = '';
		element.style.opacity = '0';
		element.style.pointerEvents = 'none';
		observer.observe(element);
	});
}

// app.js is loaded with `defer`, so DOMContentLoaded may have already fired.
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', _aosInit);
} else {
	_aosInit();
}
